import classNames from 'classnames';
import React, { useContext, useRef } from 'react';
import { Popover } from '@meitu/saas-ui';
import { isPad } from '@meitu/saas-editor-core/es/utils/browser-kit';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { EditorContext, useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '../../context';
import { ShapeTextPanel } from '../../panels';
import { useStore } from '../../store';
import { ShapeTextMaterialRaw } from '../../types';
import './style.less';

type Props = React.ComponentProps<'div'> & {
    shapeTextList: ShapeTextMaterialRaw[];
};

export const ShapeTextEntry: React.FC<Props> = ({ shapeTextList, style }) => {
    const context = useContext(TextPanelContext)!;
    const { theme } = context;

    const { $t } = useLocaleStore();
    const editor = useEditor();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const popoverRef = useRef<any>(null);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { shapeExtension, advancedTextMaterial } = layerData;
        return { shapeExtension, advancedTextMaterial };
    });
    const shapeExtensionCache = useStore((state) => state.shapeExtensionCache);

    const shapeExtension = props ? props.shapeExtension : null;
    const advancedTextMaterial = props ? props.advancedTextMaterial : null;

    const thumbnail = (() => {
        const data = shapeExtension || shapeExtensionCache;

        if (!data) return null;

        const material = shapeTextList.find((item) => item['shape_type'] === data.curveTextType);
        if (!material) return null;

        const lightThumbnail = material.thumbnail;
        const darkThumbnail = material['thumbnail_black'];

        if (theme === 'dark') return darkThumbnail || lightThumbnail;
        return lightThumbnail;
    })();

    const disabled = !!advancedTextMaterial;

    const rootCls = classNames('modal-panel-entry', { 'modal-panel-entry--disabled': disabled || isPad });

    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled || isPad) return e.stopPropagation();
    };

    return (
        <Popover
            trigger="click"
            placement="left"
            ref={popoverRef}
            content={
                <EditorContext.Provider value={editor}>
                    <TextPanelContext.Provider value={context}>
                        <ShapeTextPanel
                            shapeTextList={shapeTextList}
                            onClose={() => {
                                // 模拟弹窗外点击关闭弹窗
                                popoverRef.current?.onClick?.();
                            }}
                        />
                    </TextPanelContext.Provider>
                </EditorContext.Provider>
            }
            overlayClassName="text-layer-panel-popover"
            destroyTooltipOnHide
        >
            <div className={rootCls} onClickCapture={handleClick} style={style}>
                <div className="modal-panel-entry__thumbnail modal-panel-entry__thumbnail--flex">
                    {thumbnail ? (
                        <img className="modal-panel-entry__img" src={thumbnail.url} />
                    ) : (
                        <IconFont name="SlashCircle" fontSize={28} />
                    )}
                </div>
                <div className="modal-panel-entry__title">{$t((v) => v.panel.textPanel.transform)}</div>
            </div>
        </Popover>
    );
};
